// Shared preset catalog for Mini-TTS and Realtime. No runtime imports.
// Keep src/lib/openaiVoices.ts identical; voiceRouting.test.ts checks parity.
// Gender labels are VYV's perceived-timbre classification, not OpenAI metadata.
export type OpenAiVoiceGender = "male" | "female";

export const OPENAI_VOICE_GENDER: Record<string, OpenAiVoiceGender> = {
  marin: "female",
  cedar: "male",
  alloy: "male",
  echo: "male",
  shimmer: "female",
  ash: "male",
  ballad: "male",
  coral: "female",
  sage: "female",
  verse: "male",
};

export const DEFAULT_OPENAI_MALE = "cedar";

export const DEFAULT_OPENAI_FEMALE = "marin";

export function isOpenAiVoice(voice: unknown): voice is string {
  return typeof voice === "string" && Object.prototype.hasOwnProperty.call(OPENAI_VOICE_GENDER, voice);
}

export function defaultVoiceForGender(gender: OpenAiVoiceGender): string {
  return gender === "female" ? DEFAULT_OPENAI_FEMALE : DEFAULT_OPENAI_MALE;
}

export function genderFromAvatarUrl(avatarUrl: unknown): OpenAiVoiceGender | null {
  if (typeof avatarUrl !== "string" || avatarUrl.length === 0) return null;
  if (/\/prerendered\/female-\d+\.glb/i.test(avatarUrl)) return "female";
  if (/\/prerendered\/male-\d+\.glb/i.test(avatarUrl)) return "male";
  return null;
}

export function genderFromSex(sex: unknown): OpenAiVoiceGender | null {
  if (typeof sex !== "string") return null;
  const s = sex.trim().toLowerCase();
  if (s === "female") return "female";
  if (s === "male" || s === "other") return "male";
  return null;
}

export interface ResolveOpenAiVoiceInput {
  requested?: unknown;
  avatarUrl?: unknown;
  sex?: unknown;
}

export interface ResolvedOpenAiVoice {
  voice: string;
  source: "requested" | "avatar" | "sex" | "fallback";
}

export function resolveOpenAiVoice(
  input: ResolveOpenAiVoiceInput,
): ResolvedOpenAiVoice {
  const byAvatar = genderFromAvatarUrl(input.avatarUrl);
  // A saved voice from the previous avatar must not override the new gender.
  if (isOpenAiVoice(input.requested) &&
      (!byAvatar || OPENAI_VOICE_GENDER[input.requested] === byAvatar)) {
    return { voice: input.requested, source: "requested" };
  }

  if (byAvatar) return { voice: defaultVoiceForGender(byAvatar), source: "avatar" };

  const bySex = genderFromSex(input.sex);
  if (bySex) return { voice: defaultVoiceForGender(bySex), source: "sex" };

  return { voice: DEFAULT_OPENAI_FEMALE, source: "fallback" };
}
